import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  User,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged
} from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { UserRole, UserProfile } from '../types/user.types';

interface AuthContextType {
  currentUser: User | null;
  userProfile: UserProfile | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  register: (email: string, password: string, role: UserRole, auctionHouseName?: string) => Promise<void>;
  isAuctioneer: boolean;
  isAdmin: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      
      if (user) {
        // Load user profile
        const profileDoc = await getDoc(doc(db, 'users', user.uid));
        if (profileDoc.exists()) {
          setUserProfile(profileDoc.data() as UserProfile);
        }
      } else {
        setUserProfile(null);
      }
      
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const register = async (email: string, password: string, role: UserRole, auctionHouseName?: string) => {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    // Create user profile in Firestore
    const profile: UserProfile = {
      uid: user.uid,
      email: email,
      role: role,
      displayName: email.split('@')[0],
      ...(role === UserRole.AUCTIONEER && auctionHouseName && {
        auctionHouseName,
        auctionHouseId: `ah_${Date.now()}`
      }),
      createdAt: new Date(),
      updatedAt: new Date()
    };

    await setDoc(doc(db, 'users', user.uid), profile);
    setUserProfile(profile);

    // Initialize wallet for all users
    await setDoc(doc(db, 'wallets', user.uid), { 
      balance: role === UserRole.RINGER ? 1000 : 0 // Only Ringers get starting balance
    });

    // Initialize stats for Ringers
    if (role === UserRole.RINGER) {
      await setDoc(doc(db, 'userStats', user.uid), {
        totalPredictions: 0,
        correctPredictions: 0,
        ringerScore: 0,
        currentStreak: 0,
        longestStreak: 0,
        rank: 0
      });
    }
  };

  const login = async (email: string, password: string) => {
    await signInWithEmailAndPassword(auth, email, password);
  };

  const logout = async () => {
    await signOut(auth);
    setUserProfile(null);
  };

  const isAuctioneer = userProfile?.role === UserRole.AUCTIONEER;
  const isAdmin = userProfile?.role === UserRole.ADMIN;

  const value = {
    currentUser,
    userProfile,
    loading,
    login,
    logout,
    register,
    isAuctioneer,
    isAdmin
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};